'use client';

import { PublicUserDocument } from '@models/user';
import React, { useState } from 'react';
import BuyAddOfferButtons from './BuyAddOfferButtons';
import { ProductDocument, Variation } from 'models/product';
import Input from '@ui/Input';
import { usePathname } from 'next/navigation';

const BuyerActionButtons = ({
  product,
  seller,
  selectedVariations,
  setErrors,
  hideOffer,
  onCustomInputsChange,
  dismissQuickView,
  isSidebar,
}: {
  product: ProductDocument;
  seller?: PublicUserDocument | null;
  selectedVariations: Variation[] | null;
  setErrors?: React.Dispatch<React.SetStateAction<string[]>>;
  hideOffer?: boolean;
  onCustomInputsChange?: (
    enabled: boolean,
    values?: Record<string, string>
  ) => void;
  dismissQuickView?: () => void;
  isSidebar?: boolean;
}) => {
  const [customInputsEnabled, setCustomInputsEnabled] = useState(false);
  const [customInputValues, setCustomInputValues] = useState<
    Record<string, string>
  >({});
  const pathname = usePathname();

  function getQtyOfVariationInStock() {
    if (product.category2 === 'Gear Combos') {
      const qty1 = product.variations?.find(
        (v) =>
          (v.is_number && v.size === selectedVariations?.[0]?.size) ||
          v.size === selectedVariations?.[1]?.size
      )?.qty;
      const qty2 = product.variations?.find(
        (v) =>
          (!v.is_number && v.size === selectedVariations?.[0]?.size) ||
          v.size === selectedVariations?.[1]?.size
      )?.qty;
      return qty1 && qty2 ? Math.min(qty1, qty2) : qty1 || qty2;
    }
    if (!selectedVariations || selectedVariations.length === 0) return 0;
    let qty = 0;
    const letter = selectedVariations.find((v) => !v.is_number && v.size);
    const number = selectedVariations.find((v) => v.is_number && v.size);
    const color = selectedVariations.find((v) => v.color);
    product.variations?.forEach((v) => {
      if (
        (v.size === letter?.size || !letter) &&
        (v.size === number?.size || !number) &&
        (v.color === color?.color || !color)
      ) {
        qty += v.qty;
      }
    });
    return qty;
  }

  const handleCustomInputChange = (fieldId: string, value: string) => {
    const newValues = { ...customInputValues, [fieldId]: value };
    setCustomInputValues(newValues);
    onCustomInputsChange?.(customInputsEnabled, newValues);
  };

  return (
    <>
      <div className="flex flex-col gap-[1.6rem]">
        {/* find the quantity in stock for the given variations selected */}

        {!!selectedVariations?.length &&
          getQtyOfVariationInStock() !== undefined &&
          product.category2 !== 'Gear Combos' &&
          !(
            product.integration_info?.type === 'shopify' &&
            getQtyOfVariationInStock() &&
            getQtyOfVariationInStock() === 9999
          ) && (
            <span className="text-[1.8rem] text-gray-500">
              {getQtyOfVariationInStock()} in stock
            </span>
          )}

        {product.custom_inputs && (
          <div className="flex flex-col gap-4">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={customInputsEnabled}
                onChange={(e) => {
                  setCustomInputsEnabled(e.target.checked);
                  onCustomInputsChange?.(e.target.checked, customInputValues);
                }}
                className="h-8 w-8 cursor-pointer rounded-[.5rem] text-brand-secondary"
              />
              <span className="text-[1.8rem] font-semibold text-[#444444]">
                Customize this item for ${product.custom_inputs.cost}
              </span>
            </label>

            {customInputsEnabled && (
              <div className="flex flex-col gap-8">
                {product.custom_inputs.fields.map((field) => (
                  <div key={field.id} className="flex flex-col gap-2">
                    <label className="text-[1.8rem] font-semibold">
                      {field.label}
                      {field.required && (
                        <span className="text-red-500">*</span>
                      )}
                    </label>
                    <Input
                      type={
                        field.type as
                          | 'text'
                          | 'number'
                          | 'email'
                          | 'tel'
                          | 'password'
                          | 'search'
                          | 'date'
                          | 'time'
                      }
                      placeholder={field.placeholder || ''}
                      maxLength={field.max_length}
                      required={field.required}
                      value={customInputValues[field.id] || ''}
                      onChange={(e) =>
                        (!field.max_length ||
                          e.target.value.length <= field.max_length) &&
                        handleCustomInputChange(field.id, e.target.value)
                      }
                      className="!rounded-[1.5rem] border border-gray-300 px-3 py-2 text-[1.6rem]"
                    />
                    {field.max_length && (
                      <span className="text-[1.5rem] text-gray-500">
                        {customInputValues[field.id]?.length || 0} /{' '}
                        {field.max_length}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        {!product.is_draft && !product.is_auction && (
          <BuyAddOfferButtons
            product={product}
            seller={seller}
            selectedVariations={selectedVariations}
            customInputs={
              customInputsEnabled
                ? {
                    enabled: true,
                    values: customInputValues,
                    cost: product.custom_inputs?.cost || 0,
                  }
                : undefined
            }
            onAddToCartError={() => {
              if (!selectedVariations || selectedVariations.length === 0) {
                setErrors?.(['Jersey', 'Pant']);
              } else if (
                product.category2 === 'Gear Combos' &&
                selectedVariations &&
                selectedVariations.length === 1
              ) {
                // find out which variation is missing
                const missingVariation = selectedVariations[0].is_number
                  ? 'Jersey'
                  : 'Pant';
                setErrors?.([missingVariation]);
              }
            }}
            hideOffer={hideOffer}
            dismissQuickView={dismissQuickView}
            // if is sidebar, pass in the my-cart page pathname to hide the action buttons (buy it now and make an offer)
            pathname={isSidebar ? '/my-cart' : pathname}
          />
        )}
      </div>
    </>
  );
};

export default BuyerActionButtons;
